<template>
  <textarea
    :id="resolvedId"
    ref="area"
    :name="name"
    :rows="rows"
    :value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :required="required"
    :maxlength="maxlength"
    :aria-invalid="invalid ? 'true' : undefined"
    :aria-describedby="describedBy"
    :class="classes"
    @input="onInput"
    @blur="$emit('blur', $event)"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { cva } from 'class-variance-authority';
import { useId } from '@/composables/useId';

const textareaVariants = cva(
  [
    'w-full rounded-md border bg-paper px-4 py-3 text-ink outline-none',
    'placeholder:text-ink-subtle transition-colors duration-150',
    'focus-visible:outline-none',
  ],
  {
    variants: {
      invalid: {
        true: 'border-stroke-danger focus-visible:shadow-[0_0_0_3px_rgba(229,57,53,0.25)]',
        false:
          'border-stroke focus-visible:border-stroke-accent focus-visible:shadow-[var(--shadow-ring-focus)]',
      },
      disabled: {
        true: 'opacity-60 cursor-not-allowed',
        false: '',
      },
      autoGrow: {
        true: 'resize-none overflow-hidden',
        false: 'resize-y',
      },
    },
    defaultVariants: {
      invalid: false,
      disabled: false,
      autoGrow: false,
    },
  },
);

interface Props {
  modelValue?: string;
  rows?: number;
  placeholder?: string;
  id?: string | null;
  name?: string;
  disabled?: boolean;
  readonly?: boolean;
  invalid?: boolean;
  required?: boolean;
  autoGrow?: boolean;
  describedBy?: string;
  maxlength?: number;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  rows: 4,
  id: null,
  disabled: false,
  readonly: false,
  invalid: false,
  required: false,
  autoGrow: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'blur', event: FocusEvent): void;
}>();

const fallbackId = useId('textarea');
const resolvedId = computed(() => props.id ?? fallbackId);

const area = ref<HTMLTextAreaElement | null>(null);

const classes = computed(() =>
  textareaVariants({
    invalid: props.invalid,
    disabled: props.disabled,
    autoGrow: props.autoGrow,
  }),
);

watch(
  () => props.modelValue,
  () => void nextTick(resize),
);

function resize(): void {
  if (!props.autoGrow) return;
  const el = area.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

function onInput(event: Event): void {
  const target = event.target as HTMLTextAreaElement;
  emit('update:modelValue', target.value);
  void nextTick(resize);
}
</script>
